/**
 * Stripe Identity verification gate helpers.
 *
 * Read by ListingPage's handleOrderSubmit (and other transaction-initiating
 * call sites) to decide whether to route the user to /identity-verification
 * before letting them proceed.
 *
 * Phase 0 v2 landmine V2-A: the Redux path for effectivePermissionSet is
 *   currentUser.effectivePermissionSet.attributes.initiateTransactions
 * NOT currentUser.effectivePermissionSet.attributes.permissions.initiateTransactions.
 * The path here matches the existing hasPermissionToInitiateTransactions helper
 * at src/util/userHelpers.js:163-165.
 */

/**
 * Returns true if the user has completed Stripe Identity verification.
 * Reads metadata.identityVerified, which is operator-controlled and written
 * by the Phase B webhook handler on identity.verification_session.verified.
 *
 * @param {Object} currentUser API entity (or null/undefined when not loaded)
 * @returns {Boolean}
 */
export const isUserVerified = currentUser => {
  return currentUser?.attributes?.profile?.metadata?.identityVerified === true;
};

/**
 * Returns true if the user must verify identity before they can transact.
 *
 * Gate fires when:
 *   - The user is NOT already verified (early-return optimization), AND
 *   - The user's effectivePermissionSet denies initiateTransactions.
 *
 * The "Restrict transaction rights" Sharetribe Console toggle creates the
 * deny-by-default state for new signups. Operators can also flip individual
 * users to deny via Manage > Users > [user] > Permissions > Edit (the
 * override path).
 *
 * On successful verification, the Phase B webhook flips the permission to
 * allow, so this helper returns false on the next currentUser refresh.
 *
 * The `_listing` parameter is unused in the universal-verification model
 * (Phase G was dropped) but the signature accepts it so future per-listing
 * logic can layer on without a breaking change.
 *
 * @param {Object} currentUser API entity (or null/undefined when not loaded)
 * @param {Object} _listing API entity (unused — reserved for future use)
 * @returns {Boolean}
 */
// eslint-disable-next-line no-unused-vars
export const isVerificationRequired = (currentUser, _listing) => {
  if (isUserVerified(currentUser)) {
    return false;
  }
  const initiateTransactionsPermission =
    currentUser?.effectivePermissionSet?.attributes?.initiateTransactions;
  return initiateTransactionsPermission === 'permission/deny';
};

/**
 * Determines whether a transaction-rights-denied user (where the existing
 * flow would render the generic NoAccessPage with `missingAccessRight:
 * 'transaction-rights'`) should be redirected to `/identity-verification`
 * instead.
 *
 * Returns `true` only when the user is an unverified customer. For
 * providers, verified-but-denied customers, and suspended customers, the
 * caller falls through to the existing NoAccessPage.
 *
 * Note: Phase C deferred (callers reverted to plain NoAccessPage). Helper
 * retained as a no-op surface for a future Phase C revisit; remove on the
 * next cleanup pass if it stays unused.
 *
 * @param {Object} currentUser API entity (or null/undefined when not loaded)
 * @returns {boolean}
 */
export const shouldUseVerificationRedirectForNoAccess = currentUser => {
  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  if (userType !== 'customer') return false;

  const suspendedReason = currentUser?.attributes?.profile?.metadata?.suspendedReason;
  if (suspendedReason === 'underage_at_verification') return false;

  return !isUserVerified(currentUser);
};
